import React, { useEffect, useRef } from 'react';
import { linkifyHttpsUrlsInElement } from '../utils/linkifyHttpsUrls';

export type LinkifiedHtmlProps<T extends React.ElementType = 'div'> = {
  as?: T;
  html: string;
} & Omit<React.ComponentPropsWithoutRef<T>, 'as' | 'html' | 'children'>;

/**
 * Renders sanitized CMS HTML and autolinks plain https?:// URLs at display time only.
 */
function LinkifiedHtmlInner<T extends React.ElementType = 'div'>(
  { as, html, className, ...rest }: LinkifiedHtmlProps<T>,
  forwardedRef: React.ForwardedRef<Element>,
) {
  const Component = (as || 'div') as React.ElementType;
  const innerRef = useRef<Element | null>(null);

  const setRef = (el: Element | null) => {
    innerRef.current = el;
    if (typeof forwardedRef === 'function') {
      forwardedRef(el);
    } else if (forwardedRef) {
      (forwardedRef as React.MutableRefObject<Element | null>).current = el;
    }
  };

  useEffect(() => {
    if (innerRef.current instanceof HTMLElement) {
      linkifyHttpsUrlsInElement(innerRef.current);
    }
  }, [html]);

  const mergedClassName = [className, 'cms-html-content'].filter(Boolean).join(' ');

  return (
    <Component
      ref={setRef}
      className={mergedClassName || undefined}
      dangerouslySetInnerHTML={{ __html: html }}
      {...rest}
    />
  );
}

const LinkifiedHtml = React.forwardRef(
  LinkifiedHtmlInner as React.ForwardRefRenderFunction<Element, LinkifiedHtmlProps>,
) as <T extends React.ElementType = 'div'>(
  props: LinkifiedHtmlProps<T> & { ref?: React.ForwardedRef<Element> },
) => React.ReactElement | null;

export default LinkifiedHtml;
